import React from 'react'
import {
  TableColumn,
  TableAction,
  SelectionConfig,
  SortingConfig,
  PaginationConfig,
  PrioritizationConfig,
  StylingConfig,
  TableData,
  PaginationHookData,
  ApiResponseData
} from '../types'

/**
 * Helper para crear configuración de columnas
 */
export const createColumn = <T extends TableData>(
  config: Partial<TableColumn<T>> & { key: keyof T; title: string }
): TableColumn<T> => ({
  align: 'center',
  sortable: false,
  ...config
})

/**
 * Helper para crear múltiples columnas fácilmente
 */
export const createColumns = <T extends TableData>(
  configs: Array<Partial<TableColumn<T>> & { key: keyof T; title: string }>
): TableColumn<T>[] => configs.map(createColumn)

/**
 * Helper para crear configuración de acciones
 */
export const createAction = <T extends TableData>(
  config: TableAction<T>
): TableAction<T> => ({
  color: 'default',
  ...config
})

/**
 * Helper para crear configuración de selección
 */
export const createSelectionConfig = <T extends TableData>(
  selectedIds: (string | number)[],
  onSelectionChange: (ids: (string | number)[]) => void,
  options?: Partial<
    Omit<SelectionConfig<T>, 'selectedIds' | 'onSelectionChange'>
  >
): SelectionConfig<T> => ({
  selectedIds,
  onSelectionChange,
  ...options
})

/**
 * Helper para crear configuración de ordenamiento
 */
export const createSortingConfig = <T extends TableData>(
  field: keyof T,
  direction: 'asc' | 'desc',
  onSortChange: (field: keyof T, direction: 'asc' | 'desc') => void
): SortingConfig<T> => ({
  field,
  direction,
  onSortChange
})

/**
 * Helper que toma los datos del hook usePagination y los convierte en PaginationConfig
 */
export const createPaginationConfig = (
  paginationHook: PaginationHookData,
  totalItems: number,
  onPageChange: (page: number, pageSize: number) => void,
  options?: {
    pageSizeOptions?: number[]
  }
): PaginationConfig => ({
  page: paginationHook.page,
  pageSize: paginationHook.pageSize,
  total: totalItems,
  onPageChange,
  pageSizeOptions: options?.pageSizeOptions || [5, 10, 20, 50]
})

/**
 * Helper para manejo automático de paginación
 * Combina hook + API response + handler en una sola función
 */
export const createAutoPaginationConfig = <T>(
  paginationHook: PaginationHookData,
  apiResponse?: ApiResponseData<T>,
  onDataRequest?: (page: number, pageSize: number) => void
): PaginationConfig | undefined => {
  if (!apiResponse || !onDataRequest) return undefined

  const handlePageChange = (page: number, pageSize: number) => {
    // Si cambia el pageSize, resetear a página 0
    const actualPage = paginationHook.pageSize !== pageSize ? 0 : page
    paginationHook.setPagination(actualPage, pageSize)
    onDataRequest(actualPage, pageSize)
  }

  return createPaginationConfig(
    paginationHook,
    apiResponse.totalItems || 0,
    handlePageChange
  )
}

/**
 * Helper para crear configuración de priorización
 */
export const createPrioritizationConfig = <T extends TableData>(
  enabled: boolean,
  onMove: (id: string | number, direction: 'top' | 'up' | 'down') => void,
  options?: Partial<Omit<PrioritizationConfig<T>, 'enabled' | 'onMove'>>
): PrioritizationConfig<T> => ({
  enabled,
  onMove,
  ...options
})

/**
 * Helper para crear configuración de estilos
 */
export const createStylingConfig = <T extends TableData>(
  config?: StylingConfig<T>
): StylingConfig<T> => ({
  dense: false,
  alternatingRows: false,
  hoverEffect: true,
  ...config
})

/**
 * Helpers para formatters comunes
 */
export const formatters = {
  // Formatear booleanos
  boolean: (value: any): string => {
    if (typeof value === 'boolean') {
      return value ? 'Activo' : 'Inactivo'
    }
    return String(value)
  },

  // Formatear fechas
  date: (value: any): string => {
    if (!value) return '-'
    // Aquí podrías usar luxon o la librería de fechas que uses
    return new Date(value).toLocaleDateString('es-ES')
  },

  // Formatear moneda
  currency: (value: any, currency = 'CLP'): string => {
    if (typeof value !== 'number') return '-'
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency
    }).format(value)
  },

  // Formatear números
  number: (value: any, decimals = 0): string => {
    if (typeof value !== 'number') return '-'
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(value)
  }
}

/**
 * Helper para crear renderizador de estado con colores
 */
export const createStatusRenderer = (
  statusConfig: Record<string, { label: string; color: string }>
) => {
  return (value: any) => {
    const config = statusConfig[value]
    if (!config) return String(value)

    return React.createElement(
      'span',
      {
        style: {
          color: config.color,
          fontWeight: 'bold'
        }
      },
      config.label
    )
  }
}
